// useSwipeGestures — touch-driven scene advance/back on mobile.
//
// On phone-sized viewports the user might not want to scroll the whole
// 700vh track to get to the next chapter. We register a passive touch
// listener that detects vertical swipes:
//
//   - Swipe up (deltaY < -50, deltaX bounded): advance to the next
//     scene (scroll forward to its midpoint).
//   - Swipe down (deltaY > 50, deltaX bounded): go back a scene.
//
// The horizontal gate (|deltaX| < 30) makes sure we don't fight
// usePlanetDrag's pointer-driven planet rotation. If the user is
// clearly swiping the planet, we don't also advance the camera.
//
// Gesture fires once per touch interaction. Subsequent taps continue
// to feel natural because we only emit on touchend once the
// thresholds are satisfied and ignore taps below them.

import { useEffect, useRef } from 'react';

import { SCENES } from './planetSections';

export interface SwipeGestureOptions {
  /** Minimum vertical distance in pixels before a swipe counts. */
  threshold?: number;
  /** Maximum horizontal distance allowed during the swipe. */
  maxHorizontal?: number;
  /** When the viewport is at least this wide, disable gestures. */
  minDesktopWidth?: number;
}

declare global {
  interface Window {
    /** Optional swipe-disabled override. */
    __swipeDisabled?: boolean;
  }
}

function scrollToProgress(target: number) {
  if (typeof window === 'undefined') return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const y = Math.max(0, Math.min(max, target * max));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' });
}

function sceneAt(target: number) {
  for (let i = SCENES.length - 1; i >= 0; i--) {
    const s = SCENES[i];
    if (target >= s.start) {
      return { idx: s.id, scene: s };
    }
  }
  return { idx: 0, scene: SCENES[0] };
}

function moveToScene(delta: -1 | 1) {
  if (typeof window === 'undefined') return;
  const cur = window.__planetProgress ?? 0;
  const curScene = sceneAt(cur);
  const next = curScene.scene.id + delta;
  if (next < 0 || next >= SCENES.length) {
    // Already at a boundary — bounce the scroll slightly so the user
    // gets feedback that nothing further is available in that
    // direction.
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (delta > 0) {
      window.scrollTo({ top: max - 1, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }
  const target = SCENES[next];
  scrollToProgress((target.start + target.end) / 2);
}

/**
 * useSwipeGestures — installs the global touch listener once. Call at
 * the app root alongside useScrollTimeline / usePlanetDrag.
 *
 * Returns nothing — the hook is purely side-effect.
 */
export function useSwipeGestures(
  opts: SwipeGestureOptions = {},
): void {
  const {
    threshold = 50,
    maxHorizontal = 30,
    minDesktopWidth = 861,
  } = opts;

  // Refs (rather than state) so the touch listener doesn't re-bind
  // every render.
  const startY = useRef<number | null>(null);
  const startX = useRef<number | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isEnabled = () => {
      if (window.__swipeDisabled) return false;
      if (window.innerWidth >= minDesktopWidth) return false;
      // Don't fight an open sheet / modal — let the sheet handle its
      // own touch sequence.
      const openSheets = document.querySelectorAll(
        '.hud-panel.is-open, .detail-card',
      );
      return openSheets.length === 0;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!isEnabled()) return;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      startY.current = t.clientY;
      startX.current = t.clientX;
      fired.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY.current === null || startX.current === null) return;
      if (fired.current) return;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      const dy = startY.current - t.clientY;
      const dxRaw = t.clientX - startX.current;
      // Only consider mostly-vertical swipes — anything too horizontal
      // belongs to usePlanetDrag.
      if (Math.abs(dxRaw) > maxHorizontal) return;
      if (dy > threshold) {
        // Finger moved up → next scene.
        fired.current = true;
        moveToScene(1);
      } else if (-dy > threshold) {
        // Finger moved down → previous scene.
        fired.current = true;
        moveToScene(-1);
      }
    };

    const reset = () => {
      startY.current = null;
      startX.current = null;
      fired.current = false;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', reset);
    document.addEventListener('touchcancel', reset);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', reset);
      document.removeEventListener('touchcancel', reset);
    };
  }, [threshold, maxHorizontal, minDesktopWidth]);
}

export default useSwipeGestures;
